import { useCallback, useEffect, useState } from "react";
import styled from "styled-components";
import { Button, Input, Modal, Tooltip, message } from "antd";
import { PlusOutlined } from "@ant-design/icons";

import { addSlideInfo, deleteSlideInfo, getSlideInfo, updateSlideInfo } from "../../api/slideAPI";
import { getLoginUser } from "../../api/user";

interface Props {
  slideId?: number;
}

interface SlideInfo {
  id?: number;
  infoId?: number;
  pathologyNumber: string;
  memo?: string;
}

const infoKey = (info: SlideInfo) => info.id ?? info.infoId;

let rolePromise: Promise<string> | null = null;
const loadRole = (): Promise<string> => {
  if (!rolePromise) {
    rolePromise = getLoginUser()
      .then((data) => data?.role ?? "")
      .catch(() => {
        rolePromise = null;
        return "";
      });
  }
  return rolePromise;
};

const SlidePathologyBadge = ({ slideId }: Props) => {
  const [isAdmin, setIsAdmin] = useState(false);
  const [infoList, setInfoList] = useState<SlideInfo[]>([]);

  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<number>(0);
  const [pathologyNumber, setPathologyNumber] = useState("");
  const [memo, setMemo] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let mounted = true;
    loadRole().then((role) => {
      if (mounted) setIsAdmin(role === "ADMIN_MAIN");
    });
    return () => {
      mounted = false;
    };
  }, []);

  const fetchInfo = useCallback(async () => {
    if (!isAdmin || !slideId) {
      setInfoList([]);
      return;
    }
    try {
      const data = await getSlideInfo(slideId);
      setInfoList(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error("slide info load error:", e);
      setInfoList([]);
    }
  }, [isAdmin, slideId]);

  useEffect(() => {
    fetchInfo();
  }, [fetchInfo]);

  const openAdd = () => {
    setEditingId(0);
    setPathologyNumber("");
    setMemo("");
    setModalOpen(true);
  };

  const openEdit = (info: SlideInfo) => {
    setEditingId(infoKey(info) ?? 0);
    setPathologyNumber(info.pathologyNumber ?? "");
    setMemo(info.memo ?? "");
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!slideId) return;
    if (!pathologyNumber.trim()) {
      message.warning("Pathology Number를 입력해주세요.");
      return;
    }
    setSaving(true);
    try {
      if (editingId === 0) await addSlideInfo(slideId, pathologyNumber.trim(), memo);
      else await updateSlideInfo(editingId, pathologyNumber.trim(), memo);
      await fetchInfo();
      setModalOpen(false);
      message.success(editingId === 0 ? "병리정보가 등록되었습니다." : "병리정보가 수정되었습니다.");
    } catch (e: any) {
      message.error(e?.message || "저장에 실패했습니다.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!editingId) return;
    if (!window.confirm("해당 Pathology Information을 삭제하시겠습니까?")) return;
    setSaving(true);
    try {
      await deleteSlideInfo(editingId);
      await fetchInfo();
      setModalOpen(false);
      message.success("병리정보가 삭제되었습니다.");
    } catch (e: any) {
      message.error(e?.message || "삭제에 실패했습니다.");
    } finally {
      setSaving(false);
    }
  };

  if (!isAdmin || !slideId) return null;

  return (
    <>
      <BadgeRow>
        {infoList.map((info, i) => (
          <Tooltip
            key={infoKey(info) ?? i}
            placement="top"
            title={
              <TooltipBody>
                {info.memo?.trim() ? <Memo>{info.memo}</Memo> : <Memo $muted>메모 없음</Memo>}
                <Hint>클릭하면 수정</Hint>
              </TooltipBody>
            }
          >
            <Badge onClick={() => openEdit(info)}>{info.pathologyNumber}</Badge>
          </Tooltip>
        ))}

        <Tooltip placement="top" title="병리정보 등록 (관리자 전용)">
          <AddBadge onClick={openAdd} aria-label="병리정보 등록">
            <PlusOutlined />
          </AddBadge>
        </Tooltip>
      </BadgeRow>

      <Modal
        open={modalOpen}
        title={editingId === 0 ? "Pathology Information 등록" : "Pathology Information 수정"}
        width={400}
        centered
        onCancel={() => setModalOpen(false)}
        maskClosable={!saving}
        footer={
          <ModalFooter>
            {editingId !== 0 && (
              <Button danger type="text" size="small" disabled={saving} onClick={handleDelete}>
                삭제
              </Button>
            )}
            <FooterRight>
              <Button size="small" disabled={saving} onClick={() => setModalOpen(false)}>
                취소
              </Button>
              <Button size="small" type="primary" loading={saving} onClick={handleSave}>
                저장
              </Button>
            </FooterRight>
          </ModalFooter>
        }
      >
        <FieldLabel>Pathology Number</FieldLabel>
        <Input
          size="small"
          value={pathologyNumber}
          placeholder="병리번호를 입력해주세요"
          onChange={(e) => setPathologyNumber(e.target.value)}
          onPressEnter={handleSave}
          autoFocus
        />
        <FieldLabel style={{ marginTop: 12 }}>Memo</FieldLabel>
        <Input.TextArea
          rows={4}
          value={memo}
          placeholder="메모 (배지에 커서를 올리면 표시됩니다)"
          onChange={(e) => setMemo(e.target.value)}
        />
      </Modal>
    </>
  );
};

const BadgeRow = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
`;

const Badge = styled.span`
  display: inline-block;
  padding: 1px 7px;
  border-radius: 4px;
  background: #942404;
  color: #fff;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  line-height: 1.5;
  cursor: pointer;
  white-space: nowrap;
  transition: opacity 0.15s ease;

  &:hover {
    opacity: 0.82;
  }
`;

const AddBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 4px;
  border: 1px dashed #d9b3aa;
  color: #942404;
  font-size: 9px;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease;

  &:hover {
    background: #fdf1ee;
    border-color: #942404;
  }
`;

const TooltipBody = styled.div`
  font-size: 0.8rem;
  line-height: 1.5;
`;

const Memo = styled.div<{ $muted?: boolean }>`
  white-space: pre-wrap;
  word-break: break-word;
  ${({ $muted }) => $muted && "opacity: 0.65;"}
`;

const Hint = styled.div`
  margin-top: 4px;
  padding-top: 4px;
  border-top: 1px solid rgba(255, 255, 255, 0.2);
  font-size: 0.7rem;
  opacity: 0.7;
`;

const FieldLabel = styled.div`
  margin-bottom: 4px;
  font-size: 0.75rem;
  font-weight: 600;
  color: #8c8c8c;
`;

const ModalFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const FooterRight = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
`;

export default SlidePathologyBadge;
